import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartColumn } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import darkGrid from "../../img/about/dark-grid.svg";
import iconDollar from "../../img/about/icon-dollar.svg";
import iconFlash from "../../img/about/icon-flash.svg";
import iconHeadset from "../../img/about/icon-headset.svg";
import securityArrowLeft from "../../img/experience/security-arrow-left.svg";
import securityArrowRight from "../../img/experience/security-arrow-right.svg";

const CARDS = [
  { titleKey: "about.experience.c1.title", descKey: "about.experience.c1.desc", icon: <img src={iconDollar} alt="" className="w-[26px] h-[26px]" /> },
  { titleKey: "about.experience.c2.title", descKey: "about.experience.c2.desc", icon: <img src={iconFlash} alt="" className="w-[26px] h-[26px]" /> },
  { titleKey: "about.experience.c3.title", descKey: "about.experience.c3.desc", icon: <ChartColumn size={26} color="#fff" /> },
  { titleKey: "about.experience.c4.title", descKey: "about.experience.c4.desc", icon: <img src={iconHeadset} alt="" className="w-[26px] h-[26px]" /> },
];

function Card({ titleKey, descKey, icon }) {
  const { t } = useTranslation();
  return (
    <div className="bg-[#ee5414] rounded-[20px] p-[24px] xl:p-[40px] min-h-[220px] xl:h-[315px] flex flex-col justify-between gap-[30px]">
      <div className="bg-[#0a0a0a] rounded-full w-[52px] h-[52px] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex flex-col gap-[12px]">
        <h3 className="font-['Montserrat'] font-extrabold text-[18px] xl:text-[24px] text-white leading-normal">
          {t(titleKey)}
        </h3>
        <p className="font-['Inter'] font-normal text-[14px] xl:text-[16px] text-[#ffb393] leading-[1.4]">
          {t(descKey)}
        </p>
      </div>
    </div>
  );
}

function Experience() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="mt relative bg-[#0a0a0a] py-[80px] xl:py-[160px] overflow-hidden">
      <img src={darkGrid} alt="" aria-hidden className="absolute left-1/2 -translate-x-1/2 top-0 w-[1512px] max-w-none h-full pointer-events-none" />

      <div className="relative z-[1] glcontainer">

        {/* Desktop: 3-col grid, title occupies first cell */}
        <div className="hidden xl:grid grid-cols-3 gap-[20px] items-center">
          <h2 className="font-['Montserrat'] font-black text-[46px] text-white tracking-[-0.92px] uppercase leading-none pr-[20px]">
            {t("about.experience.title")}
          </h2>
          <Card {...CARDS[0]} />
          <Card {...CARDS[1]} />
          <Card {...CARDS[2]} />
          <Card {...CARDS[3]} />
          <div />
        </div>

        {/* Mobile: swiper */}
        <div className="xl:hidden flex flex-col gap-[14px] min-w-0">
          <h2 className="font-['Montserrat'] font-black text-[24px] text-white tracking-[-0.92px] uppercase leading-none mb-[10px]">
            {t("about.experience.title")}
          </h2>

          <Swiper
            slidesPerView={1}
            spaceBetween={12}
            onSwiper={(swiper) => { swiperRef.current = swiper; }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            className="w-full min-w-0 overflow-hidden"
          >
            {CARDS.map((card) => (
              <SwiperSlide key={card.titleKey} className="!h-auto">
                <Card {...card} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="bg-[#1e1e1e] h-[20px] rounded-[20px] px-[14px] flex items-center justify-between">
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              className="flex items-center justify-center size-[14px] opacity-70"
              aria-label="Previous"
            >
              <img src={securityArrowLeft} alt="" aria-hidden className="size-[10px]" style={{ transform: "rotate(-135deg)" }} />
            </button>

            <div className="flex items-center gap-[6px]">
              {CARDS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => swiperRef.current?.slideTo(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === activeIndex
                      ? "bg-[#ee5414] w-[6px] h-[6px]"
                      : "bg-[#444] w-[6px] h-[6px]"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              className="flex items-center justify-center size-[14px] opacity-70"
              aria-label="Next"
            >
              <img src={securityArrowRight} alt="" aria-hidden className="size-[10px]" style={{ transform: "rotate(45deg)" }} />
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Experience;
